import { useState } from 'react';
import { formatIstLabelled as formatIst, istInputToUtcIso, istInputValue, scheduledPastWindow, type ScheduledMessage } from '@wa/shared';

/**
 * Choosing when, and seeing what is already waiting.
 *
 * Times are entered and shown in IST, because the people using this are in India and
 * a scheduled message is the one place a timezone mistake is silent — it does not
 * fail, it simply arrives at the wrong hour.
 */
interface Props {
  windowExpiresAt: string | null;
  pending: ScheduledMessage[];
  isBusy: boolean;
  onSchedule: (whenUtcIso: string) => void;
  onCancel: (id: number) => void;
  onClose: () => void;
  /**
   * Shows what is pending without offering to add more. The composer disappears
   * when the 24-hour window shuts, and without this a message already scheduled
   * would become invisible and uncancellable at exactly the moment it is most
   * likely to fail.
   */
  listOnly?: boolean;
}

export { formatIst };

export function SchedulePanel({ windowExpiresAt, pending, isBusy, onSchedule, onCancel, onClose, listOnly = false }: Props) {
  const [when, setWhen] = useState(() => istInputValue(60));

  const chosenUtc = when ? istInputToUtcIso(when) : null;
  const inPast = chosenUtc !== null && new Date(chosenUtc).getTime() <= Date.now();
  const pastWindow = chosenUtc !== null && !inPast && scheduledPastWindow(chosenUtc, windowExpiresAt);

  // w-full is load-bearing, and the reason is easy to miss: mx-auto on a flex item
  // sets auto margins on the cross axis, which cancels align-items:stretch. Without
  // it the panel sized itself to its max-content — the longest pending message on one
  // line — and rendered 768px wide inside a 390px column, with the Cancel buttons and
  // the window warning clipped off the right edge. Nothing scrolled, so the overflow
  // assertion passed and only the screenshot showed it.
  return (
    <div className="mx-auto mb-2 flex w-full max-w-3xl flex-col gap-2 rounded-lg border border-(--color-line) bg-(--color-surface) p-3">
      <div className="flex items-center gap-2">
        <h3 className="min-w-0 flex-1 truncate text-small font-semibold">
          {listOnly ? 'Waiting to send' : 'Send later'}
        </h3>
        {!listOnly && <button
          type="button"
          onClick={onClose}
          aria-label="Close scheduling"
          className="tap grid size-7 shrink-0 place-items-center rounded text-(--color-muted) hover:bg-(--color-sunken)"
        >
          <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
            <path d="M6 6l8 8M14 6l-8 8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>}
      </div>

      {pending.length > 0 && (
        <ul className="flex flex-col gap-1">
          {pending.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded bg-(--color-sunken) px-2 py-1.5">
              <span className="shrink-0 text-small font-medium tabular-nums">{formatIst(item.scheduledFor)}</span>
              <span className="min-w-0 flex-1 truncate text-small text-(--color-muted)">
                {item.body ?? `[${item.messageType}]`}
              </span>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onCancel(item.id)}
                className="tap shrink-0 rounded border border-(--color-line) px-2 py-0.5 text-micro text-(--color-muted) hover:text-(--color-bad)"
              >
                Cancel
              </button>
            </li>
          ))}
        </ul>
      )}

      {!listOnly && <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-44 flex-1 flex-col gap-1">
          <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">
            When (IST)
          </span>
          <input
            type="datetime-local"
            value={when}
            min={istInputValue(1)}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full min-w-0 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          />
        </label>

        <button
          type="button"
          disabled={isBusy || chosenUtc === null || inPast}
          onClick={() => chosenUtc && onSchedule(chosenUtc)}
          className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
        >
          Schedule
        </button>
      </div>}

      {!listOnly && inPast && (
        <p className="text-small text-(--color-bad)">That time has already passed.</p>
      )}

      {/* A warning, never a refusal: the customer may well write again before then,
          which reopens the window. Forbidding something likely to work is worse than
          mentioning it. */}
      {!listOnly && pastWindow && (
        <p role="status" className="text-small text-(--color-warn)">
          The 24-hour window {windowExpiresAt ? 'closes' : 'is already closed'}
          {windowExpiresAt ? ` at ${formatIst(windowExpiresAt)}` : ''}, before this is due.
          Unless {`they`} write again first, WhatsApp will refuse it and it will fail with
          that reason — send a template instead if it has to arrive.
        </p>
      )}
    </div>
  );
}
